import { html, nothing } from "lit";
import { formatRelativeTimestamp } from "../format.ts";
import { t } from "../i18n.ts";
import type { QueueLaneInfo, QueueStatusResult } from "../controllers/queue-status.ts";

export type DashboardNode = {
  nodeId: string;
  displayName?: string;
  platform?: string;
  remoteIp?: string;
  caps?: string[];
  commands?: string[];
  connected?: boolean;
  paired?: boolean;
  connectedAtMs?: number;
  version?: string;
  coreVersion?: string;
};

export type DashboardAgent = {
  id: string;
  identity?: { name?: string; avatar?: string; avatarUrl?: string };
};

export type DashboardProps = {
  connected: boolean;
  nodesLoading: boolean;
  nodes: DashboardNode[];
  agentsLoading: boolean;
  agents: DashboardAgent[];
  sessions: { count?: number; active?: number } | null;
  presenceCount: number;
  queueStatus: QueueStatusResult | null;
  queueStatusLoading: boolean;
  onRefresh: () => void;
};

function statusDot(connected: boolean) {
  const color = connected ? "var(--success, #34d399)" : "var(--text-muted, #666)";
  return html`<span style="
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${color};
    margin-right: 8px;
    flex-shrink: 0;
    ${connected ? "box-shadow: 0 0 6px rgba(52, 211, 153, 0.5);" : ""}
  "></span>`;
}

function pulseStyle() {
  return html`
    <style>
      @keyframes dash-pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.6;
        }
      }
      .dash-live-dot {
        animation: dash-pulse 2s ease-in-out infinite;
      }
    </style>
  `;
}

export function renderDashboard(props: DashboardProps) {
  const connectedNodes = props.nodes.filter((n) => n.connected);
  const disconnectedNodes = props.nodes.filter((n) => !n.connected);
  const totalNodes = props.nodes.length;
  const agentCount = props.agents.length;
  const now = Date.now();

  return html`
    ${pulseStyle()}

    <!-- Summary Stats -->
    <section class="grid grid-cols-3" style="margin-bottom: 20px;">
      <div class="card stat-card">
        <div class="stat-label">${t("dashboard.nodes")}</div>
        <div class="stat-value" style="display: flex; align-items: baseline; gap: 8px;">
          <span style="color: var(--success, #34d399);">${connectedNodes.length}</span>
          <span style="font-size: 14px; color: var(--text-muted, #666);">/ ${totalNodes}</span>
        </div>
        <div class="muted" style="margin-top: 4px;">${t("dashboard.nodes_online")}</div>
      </div>

      <div class="card stat-card">
        <div class="stat-label">${t("dashboard.agents")}</div>
        <div class="stat-value">${agentCount}</div>
        <div class="muted" style="margin-top: 4px;">${t("dashboard.agents_registered")}</div>
      </div>

      <div class="card stat-card">
        <div class="stat-label">${t("dashboard.devices")}</div>
        <div class="stat-value">${props.presenceCount}</div>
        <div class="muted" style="margin-top: 4px;">${t("dashboard.devices_connected")}</div>
      </div>
    </section>

    <!-- Queue Status -->
    ${renderQueueStatus(props.queueStatus, props.queueStatusLoading)}

    <!-- Realtime indicator + Refresh -->
    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span class="dash-live-dot" style="
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--success, #34d399);
          box-shadow: 0 0 6px rgba(52, 211, 153, 0.5);
        "></span>
        <span style="font-size: 12px; color: var(--text-muted, #888); text-transform: uppercase; letter-spacing: 0.5px;">
          ${t("dashboard.realtime")}
        </span>
      </div>
      <button class="btn" style="font-size: 12px; padding: 4px 12px;" @click=${() => props.onRefresh()}>
        ${t("dashboard.refresh")}
      </button>
    </div>

    <!-- Connected Nodes -->
    <section class="card" style="margin-bottom: 16px;">
      <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
        ${t("dashboard.connected_nodes")}
        <span style="
          background: var(--success, #34d399);
          color: #000;
          font-size: 11px;
          font-weight: 600;
          padding: 1px 8px;
          border-radius: 10px;
        ">${connectedNodes.length}</span>
      </div>

      ${
        props.nodesLoading && connectedNodes.length === 0
          ? html`<div class="muted" style="padding: 16px 0;">${t("dashboard.loading")}</div>`
          : connectedNodes.length === 0
            ? html`<div class="muted" style="padding: 16px 0;">${t("dashboard.no_nodes")}</div>`
            : html`
                <div style="margin-top: 12px; display: flex; flex-direction: column; gap: 8px;">
                  ${connectedNodes.map(
                    (node) => html`
                      <div style="
                        background: rgba(255,255,255,0.03);
                        border: 1px solid rgba(255,255,255,0.06);
                        border-radius: var(--radius-sm, 8px);
                        padding: 12px 16px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        transition: background 0.15s;
                      "
                      @mouseover=${(e: Event) => ((e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.06)")}
                      @mouseout=${(e: Event) => ((e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)")}
                      >
                        ${statusDot(true)}
                        <div style="flex: 1; min-width: 0;">
                          <div style="font-weight: 600; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            ${node.displayName || node.nodeId.slice(0, 12)}
                          </div>
                          <div style="font-size: 12px; color: var(--text-muted, #888); margin-top: 2px;">
                            ${node.platform ?? ""} ${node.remoteIp ? `· ${node.remoteIp}` : ""}
                          </div>
                        </div>
                        <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                          ${(node.caps ?? []).map(
                            (cap) => html`
                              <span style="
                                background: rgba(108,140,255,0.12);
                                color: var(--accent, #6c8cff);
                                font-size: 11px;
                                padding: 2px 8px;
                                border-radius: 4px;
                                white-space: nowrap;
                              ">${cap}</span>
                            `,
                          )}
                        </div>
                        <div style="text-align: right; flex-shrink: 0;">
                          <div style="font-size: 11px; color: var(--text-muted, #888);">
                            ${node.connectedAtMs ? formatRelativeTimestamp(node.connectedAtMs) : ""}
                          </div>
                          <div style="font-size: 11px; color: var(--text-muted, #666); margin-top: 2px;">
                            ${(node.commands ?? []).length} ${t("dashboard.commands")}
                          </div>
                        </div>
                      </div>
                    `,
                  )}
                </div>
              `
      }
    </section>

    <!-- Node Commands Detail -->
    ${
      connectedNodes.length > 0
        ? html`
            <section class="card" style="margin-bottom: 16px;">
              <div class="card-title">${t("dashboard.node_capabilities")}</div>
              <div class="card-sub">${t("dashboard.node_capabilities_sub")}</div>
              <div style="margin-top: 12px; overflow-x: auto;">
                <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                  <thead>
                    <tr style="border-bottom: 1px solid rgba(255,255,255,0.08);">
                      <th style="text-align: left; padding: 8px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.node")}</th>
                      <th style="text-align: left; padding: 8px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.status")}</th>
                      <th style="text-align: left; padding: 8px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">IP</th>
                      <th style="text-align: left; padding: 8px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.capabilities")}</th>
                      <th style="text-align: left; padding: 8px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.uptime")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${connectedNodes.map(
                      (node) => html`
                        <tr style="border-bottom: 1px solid rgba(255,255,255,0.04);">
                          <td style="padding: 10px 12px; font-weight: 500;">
                            ${node.displayName || node.nodeId.slice(0, 12)}
                          </td>
                          <td style="padding: 10px 12px;">
                            <span style="
                              display: inline-flex;
                              align-items: center;
                              gap: 6px;
                              color: var(--success, #34d399);
                              font-size: 12px;
                            ">
                              ${statusDot(true)} Online
                            </span>
                          </td>
                          <td style="padding: 10px 12px; font-family: monospace; font-size: 12px; color: var(--text-muted, #888);">
                            ${node.remoteIp ?? "-"}
                          </td>
                          <td style="padding: 10px 12px;">
                            <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                              ${(node.commands ?? []).map(
                                (cmd) => html`
                                  <span style="
                                    background: rgba(255,255,255,0.05);
                                    font-size: 11px;
                                    padding: 2px 6px;
                                    border-radius: 3px;
                                    font-family: monospace;
                                    color: var(--text-muted, #aaa);
                                  ">${cmd}</span>
                                `,
                              )}
                            </div>
                          </td>
                          <td style="padding: 10px 12px; font-size: 12px; color: var(--text-muted, #888);">
                            ${node.connectedAtMs ? formatRelativeTimestamp(node.connectedAtMs) : "-"}
                          </td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          `
        : nothing
    }

    <!-- Disconnected Nodes -->
    ${
      disconnectedNodes.length > 0
        ? html`
            <section class="card" style="margin-bottom: 16px; opacity: 0.7;">
              <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
                ${t("dashboard.disconnected_nodes")}
                <span style="
                  background: rgba(255,255,255,0.08);
                  color: var(--text-muted, #888);
                  font-size: 11px;
                  font-weight: 600;
                  padding: 1px 8px;
                  border-radius: 10px;
                ">${disconnectedNodes.length}</span>
              </div>
              <div style="margin-top: 12px; display: flex; flex-direction: column; gap: 6px;">
                ${disconnectedNodes.map(
                  (node) => html`
                    <div style="
                      padding: 8px 16px;
                      display: flex;
                      align-items: center;
                      gap: 12px;
                      font-size: 13px;
                      color: var(--text-muted, #888);
                    ">
                      ${statusDot(false)}
                      <span>${node.displayName || node.nodeId.slice(0, 12)}</span>
                      <span style="font-family: monospace; font-size: 11px;">${node.remoteIp ?? ""}</span>
                    </div>
                  `,
                )}
              </div>
            </section>
          `
        : nothing
    }

    <!-- Agents -->
    ${
      props.agents.length > 0
        ? html`
            <section class="card">
              <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
                ${t("dashboard.agents_list")}
                <span style="
                  background: rgba(108,140,255,0.15);
                  color: var(--accent, #6c8cff);
                  font-size: 11px;
                  font-weight: 600;
                  padding: 1px 8px;
                  border-radius: 10px;
                ">${agentCount}</span>
              </div>
              <div style="margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px;">
                ${props.agents.map(
                  (agent) => html`
                    <div style="
                      background: rgba(255,255,255,0.03);
                      border: 1px solid rgba(255,255,255,0.06);
                      border-radius: var(--radius-sm, 8px);
                      padding: 10px 16px;
                      display: flex;
                      align-items: center;
                      gap: 10px;
                      min-width: 180px;
                    ">
                      ${
                        agent.identity?.avatarUrl || agent.identity?.avatar
                          ? html`<img
                              src=${agent.identity.avatarUrl || agent.identity.avatar || ""}
                              alt=""
                              style="width: 28px; height: 28px; border-radius: 50%; object-fit: cover;"
                            />`
                          : html`<div style="
                              width: 28px;
                              height: 28px;
                              border-radius: 50%;
                              background: rgba(108,140,255,0.15);
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              font-size: 13px;
                              color: var(--accent, #6c8cff);
                              font-weight: 600;
                            ">${(agent.identity?.name ?? agent.id).charAt(0).toUpperCase()}</div>`
                      }
                      <div>
                        <div style="font-weight: 500; font-size: 13px;">${agent.identity?.name ?? agent.id}</div>
                        <div style="font-size: 11px; color: var(--text-muted, #888); font-family: monospace;">${agent.id}</div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            </section>
          `
        : nothing
    }
  `;
}

// ---------------------------------------------------------------------------
// Queue Status Card
// ---------------------------------------------------------------------------

function laneStatusColor(lane: QueueLaneInfo): string {
  if (lane.active > 0 && lane.depth > 0) {
    return "var(--warning, #fbbf24)";
  }
  if (lane.active > 0) {
    return "var(--success, #34d399)";
  }
  return "var(--text-muted, #666)";
}

function renderQueueStatus(status: QueueStatusResult | null, loading: boolean) {
  if (!status && !loading) {
    return nothing;
  }

  const lanes = status ? Object.entries(status.lanes) : [];
  const activeLanes = lanes.filter(([, l]) => l.active > 0 || l.depth > 0);
  const idleLanes = lanes.filter(([, l]) => l.active === 0 && l.depth === 0);
  const totalActive = status?.totalActive ?? 0;
  const totalDepth = status?.totalDepth ?? 0;

  return html`
    <section class="card" style="margin-bottom: 16px;">
      <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
        ${t("dashboard.queue_title")}
        ${
          totalActive > 0
            ? html`<span style="
                background: rgba(52, 211, 153, 0.15);
                color: var(--success, #34d399);
                font-size: 11px;
                font-weight: 600;
                padding: 1px 8px;
                border-radius: 10px;
              ">${totalActive} ${t("dashboard.queue_active")}</span>`
            : nothing
        }
        ${
          totalDepth > 0
            ? html`<span style="
                background: rgba(251, 191, 36, 0.15);
                color: var(--warning, #fbbf24);
                font-size: 11px;
                font-weight: 600;
                padding: 1px 8px;
                border-radius: 10px;
              ">${totalDepth} ${t("dashboard.queue_queued")}</span>`
            : nothing
        }
      </div>
      <div class="card-sub">${t("dashboard.queue_sub")}</div>

      ${
        loading && lanes.length === 0
          ? html`<div class="muted" style="padding: 12px 0;">${t("dashboard.loading")}</div>`
          : lanes.length === 0
            ? html`<div class="muted" style="padding: 12px 0;">${t("dashboard.queue_idle")}</div>`
            : html`
                <div style="margin-top: 12px; overflow-x: auto;">
                  <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                    <thead>
                      <tr style="border-bottom: 1px solid rgba(255,255,255,0.08);">
                        <th style="text-align: left; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.queue_lane")}</th>
                        <th style="text-align: center; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.queue_active")}</th>
                        <th style="text-align: center; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.queue_queued")}</th>
                        <th style="text-align: center; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("dashboard.queue_concurrency")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${activeLanes.map(
                        ([name, lane]) => html`
                          <tr style="border-bottom: 1px solid rgba(255,255,255,0.04);">
                            <td style="padding: 8px 12px;">
                              <span style="display: inline-flex; align-items: center; gap: 6px;">
                                <span style="
                                  display: inline-block;
                                  width: 8px;
                                  height: 8px;
                                  border-radius: 50%;
                                  background: ${laneStatusColor(lane)};
                                  ${lane.active > 0 ? "box-shadow: 0 0 6px rgba(52, 211, 153, 0.4);" : ""}
                                "></span>
                                <span style="font-family: monospace; font-size: 12px;">${name}</span>
                              </span>
                            </td>
                            <td style="text-align: center; padding: 8px 12px; font-weight: 600; color: ${lane.active > 0 ? "var(--success, #34d399)" : "var(--text-muted, #888)"};">
                              ${lane.active}
                            </td>
                            <td style="text-align: center; padding: 8px 12px; font-weight: 600; color: ${lane.depth > 0 ? "var(--warning, #fbbf24)" : "var(--text-muted, #888)"};">
                              ${lane.depth}
                            </td>
                            <td style="text-align: center; padding: 8px 12px; font-size: 12px; color: var(--text-muted, #888);">
                              ${lane.maxConcurrent}
                            </td>
                          </tr>
                        `,
                      )}
                      ${
                        idleLanes.length > 0
                          ? html`
                              <tr style="border-bottom: 1px solid rgba(255,255,255,0.04);">
                                <td colspan="4" style="padding: 6px 12px; font-size: 12px; color: var(--text-muted, #666);">
                                  ${t("dashboard.queue_idle_lanes")}: ${idleLanes.map(([name]) => name).join(", ")}
                                </td>
                              </tr>
                            `
                          : nothing
                      }
                    </tbody>
                  </table>
                </div>
              `
      }
    </section>
  `;
}
